import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";
import { api, fileToPhotoPayload } from "@/lib/api";
import { Btn, Field, Input, Textarea, Select, ModalShell } from "@/components/kit";
import { SERVICE_TYPES, TYRE_POSITIONS, TYRE_ISSUES, GLASS_AREAS, GLASS_DAMAGE, HIRE_VEHICLE_TYPES, HIRE_CLASSES, HIRE_PROVISIONAL_NOTE } from "@/lib/vda";

// Fleet-Manager New Request. Reuses the same request schema/categories as Driver
// Mode. The FM is the authenticated REQUESTER; the AFFECTED driver (name+mobile)
// is captured separately (prefilled from the active allocation, confirmable).
export function NewRequestModal({ vehicle, hirePolicy, prefillDriver, onClose, onCreated, createFn = api.fmBooking, context = "fm" }) {
  const categories = useMemo(() => {
    const base = [
      { value: "repair", label: "Service & Repair" },
      { value: "tyres", label: "Tyres" },
      { value: "windscreen", label: "Windscreen & Glass" },
      { value: "accident", label: "Accident & Bodywork" },
      { value: "other", label: "General enquiry" },
    ];
    if (hirePolicy !== "unavailable") base.splice(4, 0, { value: "hire_vehicle", label: "Hire vehicle" });
    return base;
  }, [hirePolicy]);

  const [cat, setCat] = useState("repair");
  const [driverName, setDriverName] = useState(prefillDriver?.name || "");
  const [driverMobile, setDriverMobile] = useState(prefillDriver?.mobile || "");
  const [photos, setPhotos] = useState([]);
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);
  // shared/dynamic fields
  const [f, setF] = useState({
    service_type: "full_service", mileage: "", date1: "", description: "",
    positions: [], issue: "puncture", tyre_size: "", load_rating: "", speed_rating: "",
    glass_area: "windscreen", glass_damage: "chip",
    hire_type: "car", hire_class: "small", from_date: "", to_date: "",
    acc_date: "", acc_time: "", acc_location: "", acc_postcode: "", acc_road: "dry", acc_how: "",
    acc_tp_name: "", acc_tp_reg: "", acc_tp_insurer: "", acc_injuries: "none", acc_police: false, acc_police_ref: "",
  });
  const set = (k, val) => setF((s) => ({ ...s, [k]: val }));
  const togglePos = (p) => setF((s) => ({ ...s, positions: s.positions.includes(p) ? s.positions.filter((x) => x !== p) : [...s.positions, p] }));

  const submit = async () => {
    // Sprint 6: Accident & Bodywork must validate before save (no silent no-op).
    if (cat === "accident") {
      const missing = [];
      if (!f.acc_date) missing.push("incident date");
      if (!f.acc_how || !f.acc_how.trim()) missing.push("how it occurred");
      if (missing.length) {
        setFormErr(`Please complete: ${missing.join(", ")}.`);
        toast.error(`Please complete the required accident details: ${missing.join(", ")}.`);
        return;
      }
    }
    setFormErr("");
    if (cat === "tyres") {
      if (f.positions.length === 0) return toast.error("Select at least one tyre position.");
      if (!f.tyre_size.trim() || !f.load_rating.trim() || !f.speed_rating.trim()) return toast.error("Tyre size, load and speed ratings are required.");
    }
    if (cat === "other" && !f.description.trim()) return toast.error("Please add some detail.");
    if (cat === "accident" && (!f.acc_date || !f.acc_how.trim())) return toast.error("Incident date and how it happened are required.");
    setSaving(true);
    try {
      let request_type = cat, request_category = cat, request_details = { flow: cat }, extra = {};
      if (cat === "repair") {
        const st = SERVICE_TYPES.find((s) => s.value === f.service_type) || SERVICE_TYPES[0];
        request_type = st.request_type; request_category = "service_repair";
        request_details = { flow: "service_repair", service_type: f.service_type, service_type_label: st.label };
        extra = { mileage: f.mileage ? Number(f.mileage) : undefined, preferred_date_1: f.date1 || undefined };
      } else if (cat === "tyres") {
        request_details = { flow: "tyres", positions: f.positions, issue: f.issue,
          issue_label: TYRE_ISSUES.find((i) => i.value === f.issue)?.label,
          tyre_size: f.tyre_size.trim(), load_rating: f.load_rating.trim(), speed_rating: f.speed_rating.trim().toUpperCase() };
      } else if (cat === "windscreen") {
        request_details = { flow: "windscreen_glass", area: f.glass_area, damage: f.glass_damage,
          area_label: GLASS_AREAS.find((a) => a.value === f.glass_area)?.label,
          damage_label: GLASS_DAMAGE.find((d) => d.value === f.glass_damage)?.label };
      } else if (cat === "hire_vehicle") {
        const cls = (HIRE_CLASSES[f.hire_type] || []).find((c) => c.value === f.hire_class);
        request_details = { flow: "hire_vehicle", vehicle_type: f.hire_type, vehicle_class: f.hire_class,
          vehicle_class_label: cls?.label, provisional_classes: true, from_date: f.from_date || null, to_date: f.to_date || null,
          hire_kind: hirePolicy === "required" ? "required" : "requested" };
        extra = { preferred_date_1: f.from_date || undefined };
      } else if (cat === "accident") {
        request_type = "accident"; request_category = "accident_bodywork";
        request_details = { flow: "accident_bodywork", incident_date: f.acc_date, incident_time: f.acc_time || null,
          location: f.acc_location || null, postcode: f.acc_postcode || null, road_conditions: f.acc_road,
          how_occurred: f.acc_how,
          third_party: { name: f.acc_tp_name || null, registration: f.acc_tp_reg || null, insurer: f.acc_tp_insurer || null },
          injuries: f.acc_injuries, police_informed: f.acc_police, police_ref: f.acc_police_ref || null };
        extra = { preferred_date_1: f.acc_date || undefined };
      }
      const { id } = await createFn({
        vehicle_id: vehicle.id, request_type, request_category, request_details,
        description: f.description || null, driver_name: driverName || null, driver_mobile: driverMobile || null,
        ...extra,
      });
      if (cat === "accident" && photos.length && id) {
        try {
          const payloads = await Promise.all(photos.map(fileToPhotoPayload));
          const res = await api.uploadBookingPhotos(id, payloads);
          if (res.uploaded < photos.length) toast.warning("Request saved; some photos could not be uploaded.");
        } catch (_) { toast.warning("Request saved, but the evidence photos could not be uploaded."); }
      }
      onCreated();
    } catch (e) { toast.error(e.message || "Could not submit the request."); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} className="p-5" testId="cv-new-modal">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">New request · {vehicle.registration?.toUpperCase()}</h2>
          <button data-testid="cv-new-close" onClick={onClose}><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>

        <div className="rounded-[6px] bg-secondary/50 p-3 text-xs text-muted-foreground">
          {context === "staff" ? (
            <>You are logging this request <strong>on behalf of the driver</strong> (e.g. an inbound call). It routes to the customer's pod queue and is assigned to you so you own the immediate action.</>
          ) : (
            <>You are raising this as the <strong>Fleet Manager</strong>. The affected driver's details below are for the garage/updates — you can follow progress here in the workspace.</>
          )}
        </div>

        <Field label="Request type">
          <Select data-testid="cv-cat" value={cat} onChange={(e) => setCat(e.target.value)}>
            {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Affected driver"><Input data-testid="cv-driver-name" value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Driver name" /></Field>
          <Field label="Driver mobile"><Input data-testid="cv-driver-mobile" value={driverMobile} onChange={(e) => setDriverMobile(e.target.value)} placeholder="07…" /></Field>
        </div>

        {cat === "repair" && (
          <>
            <Field label="What's needed?"><Select data-testid="cv-service-type" value={f.service_type} onChange={(e) => set("service_type", e.target.value)}>{SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</Select></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Current mileage"><Input data-testid="cv-mileage" inputMode="numeric" value={f.mileage} onChange={(e) => set("mileage", e.target.value.replace(/\D/g, ""))} placeholder="e.g. 42000" /></Field>
              <Field label="Preferred date"><Input data-testid="cv-date1" type="date" value={f.date1} onChange={(e) => set("date1", e.target.value)} /></Field>
            </div>
          </>
        )}

        {cat === "tyres" && (
          <>
            <div className="mb-2"><span className="vda-tick text-muted-foreground">Which tyres?</span>
              <div className="grid grid-cols-2 gap-2 mt-1">{TYRE_POSITIONS.map((p) => { const on = f.positions.includes(p.value); return (
                <button key={p.value} type="button" data-testid={`cv-tyre-${p.value}`} onClick={() => togglePos(p.value)} className={`h-11 rounded-[6px] border text-sm ${on ? "border-primary bg-primary/10 text-primary" : "border-[hsl(var(--input))]"}`}>{p.label}</button>
              ); })}</div>
            </div>
            <Field label="Issue"><Select data-testid="cv-tyre-issue" value={f.issue} onChange={(e) => set("issue", e.target.value)}>{TYRE_ISSUES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}</Select></Field>
            <div className="rounded-[6px] bg-secondary/40 p-2.5 mb-2 text-[11px] text-muted-foreground">Sidewall example: <span className="vda-reg font-semibold">205/65 R16C</span> <span className="text-primary font-semibold">107/105T</span> — size · load · speed. All three required.</div>
            <Field label="Tyre size (required)"><Input data-testid="cv-tyre-size" value={f.tyre_size} onChange={(e) => set("tyre_size", e.target.value.toUpperCase())} placeholder="205/65 R16C" className="uppercase" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Load (required)"><Input data-testid="cv-tyre-load" value={f.load_rating} onChange={(e) => set("load_rating", e.target.value)} placeholder="107/105 or 96" /></Field>
              <Field label="Speed (required)"><Input data-testid="cv-tyre-speed" value={f.speed_rating} onChange={(e) => set("speed_rating", e.target.value.toUpperCase().slice(0, 3))} placeholder="T or H" className="uppercase" /></Field>
            </div>
          </>
        )}

        {cat === "windscreen" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Glass area"><Select data-testid="cv-glass-area" value={f.glass_area} onChange={(e) => set("glass_area", e.target.value)}>{GLASS_AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</Select></Field>
            <Field label="Damage"><Select data-testid="cv-glass-damage" value={f.glass_damage} onChange={(e) => set("glass_damage", e.target.value)}>{GLASS_DAMAGE.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}</Select></Field>
          </div>
        )}

        {cat === "hire_vehicle" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vehicle type"><Select data-testid="cv-hire-type" value={f.hire_type} onChange={(e) => { set("hire_type", e.target.value); set("hire_class", (HIRE_CLASSES[e.target.value] || [])[0]?.value); }}>{HIRE_VEHICLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</Select></Field>
              <Field label="Class"><Select data-testid="cv-hire-class" value={f.hire_class} onChange={(e) => set("hire_class", e.target.value)}>{(HIRE_CLASSES[f.hire_type] || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</Select></Field>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1 mb-2">{HIRE_PROVISIONAL_NOTE}</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="From"><Input data-testid="cv-hire-from" type="date" value={f.from_date} onChange={(e) => set("from_date", e.target.value)} /></Field>
              <Field label="Until"><Input data-testid="cv-hire-to" type="date" value={f.to_date} onChange={(e) => set("to_date", e.target.value)} /></Field>
            </div>
          </>
        )}

        {cat === "accident" && (
          <div className="space-y-4" data-testid="cv-accident">
            <p className="vda-tick text-muted-foreground">Accident report</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Incident date (required)"><Input data-testid="cv-acc-date" type="date" value={f.acc_date} onChange={(e) => set("acc_date", e.target.value)} /></Field>
              <Field label="Incident time"><Input data-testid="cv-acc-time" type="time" value={f.acc_time} onChange={(e) => set("acc_time", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location"><Input data-testid="cv-acc-location" value={f.acc_location} onChange={(e) => set("acc_location", e.target.value)} placeholder="Road / junction" /></Field>
              <Field label="Postcode"><Input data-testid="cv-acc-postcode" value={f.acc_postcode} onChange={(e) => set("acc_postcode", e.target.value.toUpperCase())} className="uppercase" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Road conditions"><Select data-testid="cv-acc-road" value={f.acc_road} onChange={(e) => set("acc_road", e.target.value)}><option value="dry">Dry</option><option value="wet">Wet</option><option value="ice">Ice / snow</option><option value="other">Other</option></Select></Field>
              <Field label="Injuries"><Select data-testid="cv-acc-injuries" value={f.acc_injuries} onChange={(e) => set("acc_injuries", e.target.value)}><option value="none">None</option><option value="minor">Minor</option><option value="serious">Serious</option></Select></Field>
            </div>
            <Field label="How the incident occurred (required)"><Textarea data-testid="cv-acc-how" rows={2} value={f.acc_how} onChange={(e) => set("acc_how", e.target.value)} placeholder="Brief account of what happened" /></Field>
            <p className="vda-tick text-muted-foreground pt-1">Third party (if any)</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Name"><Input data-testid="cv-acc-tp-name" value={f.acc_tp_name} onChange={(e) => set("acc_tp_name", e.target.value)} /></Field>
              <Field label="Registration"><Input data-testid="cv-acc-tp-reg" value={f.acc_tp_reg} onChange={(e) => set("acc_tp_reg", e.target.value.toUpperCase())} className="uppercase" /></Field>
              <Field label="Insurer"><Input data-testid="cv-acc-tp-insurer" value={f.acc_tp_insurer} onChange={(e) => set("acc_tp_insurer", e.target.value)} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="cv-acc-police" checked={f.acc_police} onChange={(e) => set("acc_police", e.target.checked)} /> Police informed</label>
            {f.acc_police && <Field label="Police reference"><Input data-testid="cv-acc-police-ref" value={f.acc_police_ref} onChange={(e) => set("acc_police_ref", e.target.value)} /></Field>}
            <div>
              <span className="vda-tick text-muted-foreground">Evidence photos (optional)</span>
              <label className="mt-1 flex items-center gap-2 h-11 px-3 rounded-[6px] border border-[hsl(var(--input))] cursor-pointer text-sm text-muted-foreground">
                <Upload className="h-4 w-4" /> Add photos of the damage / scene
                <input data-testid="cv-acc-photos" type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => setPhotos((prev) => [...prev, ...Array.from(e.target.files || [])].slice(0, 10))} />
              </label>
              {photos.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="cv-acc-photo-list">
                  {photos.map((p, i) => (
                    <li key={i} className="flex items-center justify-between rounded-[6px] bg-secondary/40 px-2.5 py-1.5 text-xs">
                      <span className="truncate">{p.name}</span>
                      <button type="button" data-testid={`cv-acc-photo-remove-${i}`} onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <Field label={cat === "other" ? "Your enquiry" : "Additional information"}>
          <Textarea data-testid="cv-description" rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder={cat === "other" ? "How can we help?" : "Anything the garage should know"} />
        </Field>

        {formErr && <p className="text-sm text-[hsl(var(--vda-red))]" data-testid="cv-form-error">{formErr}</p>}
        <div className="flex gap-2 pt-1">
          <Btn variant="outline" className="flex-1" onClick={onClose}>Cancel</Btn>
          <Btn className="flex-1" data-testid="cv-new-submit" onClick={submit} loading={saving}>Submit request</Btn>
        </div>
      </div>
    </ModalShell>
  );
}
